import { Fragment } from 'react'
import type { MatchResult, SliderDetail } from '../../lib/matching/types'
import { KATEGORIE_BY_VALUE } from '../../lib/matching/engine'
import { CATEGORIES } from '../../lib/matching/catalog'

function scoreColor(score: number): string {
  if (score >= 80) return '#10B981'
  if (score >= 60) return '#3B82F6'
  if (score >= 45) return '#F59E0B'
  return '#EF4444'
}

function fmtSlider(v: number | null): string {
  if (v == null || v === undefined) return '–'
  return v > 0 ? `+${v}` : `${v}`
}

function Bar({ value, color }: { value: number; color: string }) {
  return (
    <div className="mb-bar">
      <div className="mb-bar-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
    </div>
  )
}

function EbeneCard({ label, sub, value }: { label: string; sub: string; value: number }) {
  return (
    <div className="mb-ebene">
      <span className="mb-ebene-val" style={{ color: scoreColor(value) }}>{value}</span>
      <span className="mb-ebene-label">{label}</span>
      <span className="mb-ebene-sub">{sub}</span>
    </div>
  )
}

const RETENTION_STATUS_LABEL: Record<string, string> = {
  geloest: 'gelöst',
  neutral: 'teilweise',
  risiko: 'Risiko',
  unbekannt: 'kein Pendant',
}

export default function MatchBreakdown({ result }: { result: MatchResult }) {
  const kat = KATEGORIE_BY_VALUE[result.kategorie]

  return (
    <div className="mb">
      {/* Knock-out */}
      {result.knockout && (
        <div className="mb-knockout">
          <strong>❌ Knock-out — diese Einrichtung ist nicht matchbar:</strong>
          <ul>
            {result.knockoutReasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Score-Zusammensetzung */}
      <div className="mb-section">
        <h5 className="mb-h">Score-Zusammensetzung</h5>
        <div className="mb-ebenen">
          <EbeneCard label="Hard Facts" sub="Gewicht 25 %" value={result.hardFactsScore} />
          <EbeneCard label="Arbeitsplatz-Fit" sub="Gewicht 60 %" value={result.arbeitsplatzScore} />
          <EbeneCard label="Retention-Fit" sub="Gewicht 15 %" value={result.retentionScore} />
          <div className="mb-ebene mb-ebene--risk">
            <span className="mb-ebene-val">−{result.riskPenalty}</span>
            <span className="mb-ebene-label">Risikoabzug</span>
            <span className="mb-ebene-sub">max. 25</span>
          </div>
          <div className="mb-ebene mb-ebene--total" style={{ borderColor: kat.color }}>
            <span className="mb-ebene-val" style={{ color: kat.color }}>{result.totalScore}</span>
            <span className="mb-ebene-label">Gesamt</span>
            <span className="mb-ebene-sub" style={{ color: kat.color }}>{kat.icon} {kat.label}</span>
          </div>
        </div>
        <p className="mb-formula">
          Hard Facts ({result.hardFactsScore}) × 0,25 + Arbeitsplatz ({result.arbeitsplatzScore}) × 0,60
          + Retention ({result.retentionScore}) × 0,15 − Risiko ({result.riskPenalty}) ={' '}
          <strong>{result.totalScore} / 100</strong>
        </p>
      </div>

      {/* Starke Gründe & Risiken */}
      {(result.strongReasons.length > 0 || result.risks.length > 0 || result.warnings.length > 0) && (
        <div className="mb-section mb-grid2">
          <div>
            <h5 className="mb-h">Starke Matchgründe</h5>
            {result.strongReasons.length > 0 ? (
              <ul className="mb-list">
                {result.strongReasons.map((r, i) => (
                  <li key={i} className="mb-li-good">+ {r}</li>
                ))}
              </ul>
            ) : (
              <p className="mb-empty">Keine besonders starken Treffer.</p>
            )}
          </div>
          <div>
            <h5 className="mb-h">Mögliche Risiken & Hinweise</h5>
            {result.risks.length > 0 || result.warnings.length > 0 ? (
              <ul className="mb-list">
                {result.risks.map((r, i) => (
                  <li key={`r${i}`} className="mb-li-bad">− {r}</li>
                ))}
                {result.warnings.map((w, i) => (
                  <li key={`w${i}`} className="mb-li-warn">⚠ {w}</li>
                ))}
              </ul>
            ) : (
              <p className="mb-empty">Keine Risiken erkannt.</p>
            )}
          </div>
        </div>
      )}

      {/* Detailvergleich */}
      <div className="mb-section">
        <h5 className="mb-h">Detailvergleich — Frage für Frage</h5>
        <div className="mb-table">
          <div className="mb-table-row mb-table-row--head">
            <div>Frage / Kriterium</div>
            <div>Pflegekraft</div>
            <div>Einrichtung</div>
            <div className="mb-table-score-cell">Score</div>
          </div>

          {/* HARD FACTS */}
          <SectionHead title="Hard Facts" score={result.hardFactsScore} sub="Gewicht 25 %" />
          {result.hardFactDetails.map((d) => (
            <div key={d.id} className="mb-table-row">
              <div className="mb-table-q">
                <strong>{d.label}</strong>
                <span className="mb-table-q-desc">{d.beschreibung}</span>
              </div>
              <div className="mb-table-pk">{d.pkAnswer}</div>
              <div className="mb-table-ag">{d.agAnswer}</div>
              <ScoreCell value={d.score} />
            </div>
          ))}

          {/* SLIDER NACH KATEGORIE */}
          {CATEGORIES.map((cat) => {
            const items = result.sliderDetails.filter((s) => s.category === cat.id)
            if (items.length === 0) return null
            const catScore = result.categoryScores.find((c) => c.categoryId === cat.id)
            return (
              <Fragment key={cat.id}>
                <SectionHead
                  title={`${cat.icon} ${cat.label}`}
                  score={catScore?.score ?? 0}
                  sub={`Gewicht ${cat.weight}`}
                />
                {items.map((s) => (
                  <SliderRow key={s.id} s={s} />
                ))}
              </Fragment>
            )
          })}

          {/* RETENTION / WECHSELGRÜNDE */}
          {result.retentionDetails.length > 0 && (
            <Fragment>
              <SectionHead title="💭 Wechselmotive (Retention-Fit)" score={result.retentionScore} sub="Gewicht 15 %" />
              {result.retentionDetails.map((r) => (
                <div key={r.grund} className="mb-table-row">
                  <div className="mb-table-q">
                    <strong>
                      {r.topRang && <span className="mb-toprang">#{r.topRang}</span>} {r.label}
                    </strong>
                    <span className="mb-table-q-desc">{r.beschreibung}</span>
                  </div>
                  <div className="mb-table-pk">Intensität {r.intensitaet}/5</div>
                  <div className="mb-table-ag">
                    {r.employerOffer != null ? `${r.employerOffer}/10` : '—'}
                    <span className="mb-table-status" style={{ color: scoreColor(r.score) }}>
                      {' '}({RETENTION_STATUS_LABEL[r.status] || r.status})
                    </span>
                  </div>
                  <ScoreCell value={r.score} />
                </div>
              ))}
            </Fragment>
          )}

          {/* PK-ONLY ANSWERS */}
          {result.pkOnlyAnswers.length > 0 && (
            <Fragment>
              <div className="mb-table-section-head mb-table-section-head--info">
                <span>👤 Nur Pflegekraft-Wünsche</span>
                <span className="mb-table-section-sub">Profil-Info, kein Score-Beitrag</span>
              </div>
              {result.pkOnlyAnswers.map((a) => (
                <div key={a.id} className="mb-table-row mb-table-row--info">
                  <div className="mb-table-q">{a.label}</div>
                  <div className="mb-table-pk" style={{ color: scoreColor(a.value > 0 ? 80 : a.value < 0 ? 40 : 60) }}>
                    {fmtSlider(a.value)}
                  </div>
                  <div className="mb-table-ag">—</div>
                  <div className="mb-table-score-cell mb-table-info-dash">—</div>
                </div>
              ))}
            </Fragment>
          )}
        </div>
      </div>

      {/* Gesprächsimpulse */}
      {result.gespraechsimpulse.length > 0 && (
        <div className="mb-section">
          <h5 className="mb-h">Gesprächsimpulse für die Vermittlung</h5>
          <ol className="mb-impulse">
            {result.gespraechsimpulse.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )

  function SectionHead({ title, score, sub }: { title: string; score: number; sub: string }) {
    return (
      <div className="mb-table-section-head">
        <span>{title}</span>
        <span className="mb-table-section-sub">{sub}</span>
        <span className="mb-table-section-score" style={{ color: scoreColor(score) }}>
          {score}
        </span>
      </div>
    )
  }

  function ScoreCell({ value }: { value: number }) {
    return (
      <div className="mb-table-score-cell">
        <span className="mb-table-score-val" style={{ color: scoreColor(value) }}>
          {value}
        </span>
        <Bar value={value} color={scoreColor(value)} />
      </div>
    )
  }

  function SliderRow({ s }: { s: SliderDetail }) {
    return (
      <div className="mb-table-row">
        <div className="mb-table-q">{s.label}</div>
        <div className="mb-table-pk">{fmtSlider(s.candidateValue)}</div>
        <div className="mb-table-ag">{fmtSlider(s.employerValue)}</div>
        <ScoreCell value={s.score} />
      </div>
    )
  }
}
